/**
 * ConceptStep — Step 3 of the wizard (Phase 2 UX redesign, Feb 2026).
 *
 * Before this split, the "I'm not sure — give me ideas" option mounted the
 * ConceptGenerator INSIDE the Story screen, which made the page enormously
 * tall and visually overwhelming. Phase 2 splits this out: Step 2 (Story)
 * captures the user's intent; if they pick "give me ideas", the wizard
 * advances to Step 3 (Concept) which is just this dedicated screen.
 *
 * Flow on this screen
 * ───────────────────
 *   1. ConceptGenerator collects: brand name → references (URL / social /
 *      videos / images / docs / instructions) → industry → video type →
 *      cast mode → AI-generated 3 concept cards → user picks one.
 *   2. On pick, ConceptGenerator fires `onConceptReady(conceptText, meta)`.
 *   3. We open the AudioStyleModal so the user can choose narration style,
 *      region, language, and visual style for the generated script.
 *   4. On modal confirm we call `generateScriptFromConcept`, then forward
 *      the script + meta to the parent via `onScriptReady`.
 *
 * Props
 * ─────
 *   initialData   — the activeProject snapshot (brand, industry, etc.).
 *   onScriptReady — called with `{ script, brandName, castMode, industry,
 *                   videoType, audioStyle, videoStyle, region, language }`
 *                   once the script has been generated. Parent should
 *                   stamp these on the project and advance to 'characters'.
 *   onBack        — back-button handler (typically `setStep('script')`).
 */
import React, { useState } from 'react';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { Sparkles } from 'lucide-react';
import { ConceptGenerator } from './ConceptGenerator';
import { AudioStyleModal } from './ScriptInput';
import { generateScriptFromConcept } from '../../services/geminiService';

export const ConceptStep = ({ initialData = {}, onScriptReady, onBack }) => {
    const [pickedConcept, setPickedConcept] = useState(null);
    const [pickedMeta, setPickedMeta] = useState({});
    const [showStyleModal, setShowStyleModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    const handleConceptReady = (conceptText, meta) => {
        // ConceptGenerator already produced the full picker-aware concept
        // block (brand + industry + video type + cast mode + the concept
        // itself). Stash it and open the style modal so the user can
        // pick narration/region/language before we kick off the LLM.
        setPickedConcept(conceptText);
        setPickedMeta(meta || {});
        setError(null);
        setShowStyleModal(true);
    };

    const handleStyleConfirm = async (audioStyle, region, language, videoStyle, videoPace) => {
        setShowStyleModal(false);
        if (!pickedConcept) return;
        setIsGenerating(true);
        setError(null);
        try {
            const projectDetails = {
                name: initialData.name || '',
                duration: initialData.duration || '30 seconds',
                aspectRatio: initialData.aspectRatio || '16:9',
                audioStyle,
                videoStyle,
                // Feb 2026 — Video pace (slow | standard | fast) drives the
                // LLM's shot cadence. Missed the ConceptStep flow in the
                // initial rollout — this is the "I have a rough idea →
                // pick a concept" path.
                videoPace: videoPace || initialData.videoPace || 'standard',
                // Brand scope (Feb 2026) — forwarded to /ai/generate-script-async
                // so the LLM sees only the products the user picked on Story.
                brandId:    initialData.brandId    || null,
                productIds: initialData.productIds || [],
                serviceIds: initialData.serviceIds || [],
            };
            const result = await generateScriptFromConcept(
                pickedConcept,
                audioStyle,
                region,
                language,
                projectDetails,
            );
            const scriptText = (typeof result === 'object' && result?.script) ? result.script : result;
            if (!scriptText) {
                throw new Error('No script returned by the AI. Please try again.');
            }
            onScriptReady({
                script: scriptText,
                brandName: pickedMeta.brandName,
                castMode: pickedMeta.castMode,
                audioStyle,
                videoStyle,
                videoPace: videoPace || initialData.videoPace || 'standard',
                region,
                language,
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to generate script. Please try again.');
            setIsGenerating(false);
        }
    };

    if (isGenerating) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: '#050505' }}>
                <LoadingSpinner className="w-16 h-16 mb-6 text-violet-500" />
                <h2 className="text-xl font-bold text-white mb-1">Crafting your script…</h2>
                <p className="text-sm text-zinc-400">Turning the chosen concept into a shot-ready script.</p>
            </div>
        );
    }

    return (
        <>
            {showStyleModal && (
                <AudioStyleModal
                    initialVideoStyle={initialData.videoStyle || 'realistic'}
                    initialVideoPace={initialData.videoPace || 'standard'}
                    onConfirm={handleStyleConfirm}
                    onClose={() => setShowStyleModal(false)}
                />
            )}
            <div
                className="w-full h-full overflow-y-auto flex items-start justify-center p-6 md:p-8"
                style={{ background: '#050505', fontFamily: "'Manrope', sans-serif" }}
                data-testid="concept-step"
            >
                <div className="w-full max-w-5xl">
                    <div
                        className="rounded-2xl border border-white/10 overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl"
                        style={{ background: 'rgba(15, 17, 21, 0.85)' }}
                    >
                        {/* Header */}
                        <div className="px-8 pt-8 pb-2">
                            <div className="flex items-center gap-3 mb-1">
                                <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                                    <Sparkles className="w-6 h-6 text-violet-400" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Pick a concept</h1>
                                    <p className="text-sm text-zinc-400 mt-0.5">Tell us about your brand and we&apos;ll suggest 3 video ideas. Pick the one that fits.</p>
                                </div>
                            </div>
                        </div>

                        {/* Concept generator body */}
                        <div className="px-8 pb-8 pt-2">
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-xl text-xs mb-4" data-testid="concept-step-error">
                                    {error}
                                </div>
                            )}
                            <ConceptGenerator
                                initialData={initialData}
                                onConceptReady={handleConceptReady}
                            />
                        </div>

                        {/* Footer — Back button only. ConceptGenerator's own
                            "use this concept" buttons trigger the script flow. */}
                        <div className="px-8 pb-8 -mt-2">
                            <button
                                type="button"
                                onClick={onBack}
                                className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-full px-6 py-3 text-sm font-semibold transition-all"
                                data-testid="concept-step-back-btn"
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Back to Story
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConceptStep;
